export type MessageSchedulingChannel =
  | "whatsapp"
  | "facebook"
  | "instagram";

export type ScheduledMessageStatus = "scheduled" | "cancelled";

export interface ScheduledMessageContact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

export interface ScheduledMessageMedia {
  id: string;
  name: string;
  type: string;
  size: number;
  /** Data URL for small images only (FE preview / local persistence). */
  previewDataUrl?: string;
}

export interface ScheduledMessageLink {
  url: string;
  title?: string;
  description?: string;
}

export interface ScheduledMessage {
  id: string;
  companyId: string;
  channel: MessageSchedulingChannel;
  contacts: ScheduledMessageContact[];
  text: string;
  media: ScheduledMessageMedia[];
  link?: ScheduledMessageLink;
  scheduledAt: string;
  createdAt: string;
  updatedAt?: string;
  status: ScheduledMessageStatus;
}

export interface MessageSchedulingTemplate {
  id: string;
  companyId: string;
  name: string;
  text: string;
  media: ScheduledMessageMedia[];
  link?: ScheduledMessageLink;
  createdAt: string;
  updatedAt?: string;
}

const STORAGE_KEY = "wembii.messageScheduling.v1";
const TEMPLATES_STORAGE_KEY = "wembii.messageSchedulingTemplates.v1";

export function messageSchedulingId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function readList<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeList<T>(key: string, items: T[]) {
  localStorage.setItem(key, JSON.stringify(items));
}

function normalize(item: ScheduledMessage): ScheduledMessage {
  return {
    ...item,
    contacts: Array.isArray(item.contacts) ? item.contacts : [],
    media: Array.isArray(item.media) ? item.media : [],
  };
}

export function listScheduledMessages(companyId: string): ScheduledMessage[] {
  return readList<ScheduledMessage>(STORAGE_KEY)
    .filter((item) => item.companyId === companyId)
    .map(normalize)
    .sort(
      (a, b) =>
        new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
    );
}

export function saveScheduledMessage(
  item: ScheduledMessage
): ScheduledMessage[] {
  const all = readList<ScheduledMessage>(STORAGE_KEY).filter(
    (existing) => existing.id !== item.id
  );
  all.push(item);
  writeList(STORAGE_KEY, all);
  return listScheduledMessages(item.companyId);
}

export function deleteScheduledMessage(
  companyId: string,
  id: string
): ScheduledMessage[] {
  writeList(
    STORAGE_KEY,
    readList<ScheduledMessage>(STORAGE_KEY).filter((item) => item.id !== id)
  );
  return listScheduledMessages(companyId);
}

export function listMessageTemplates(
  companyId: string
): MessageSchedulingTemplate[] {
  return readList<MessageSchedulingTemplate>(TEMPLATES_STORAGE_KEY)
    .filter((item) => item.companyId === companyId)
    .map((item) => ({
      ...item,
      media: Array.isArray(item.media) ? item.media : [],
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function saveMessageTemplate(
  item: MessageSchedulingTemplate
): MessageSchedulingTemplate[] {
  const all = readList<MessageSchedulingTemplate>(TEMPLATES_STORAGE_KEY).filter(
    (existing) => existing.id !== item.id
  );
  all.push(item);
  writeList(TEMPLATES_STORAGE_KEY, all);
  return listMessageTemplates(item.companyId);
}

export function deleteMessageTemplate(
  companyId: string,
  id: string
): MessageSchedulingTemplate[] {
  writeList(
    TEMPLATES_STORAGE_KEY,
    readList<MessageSchedulingTemplate>(TEMPLATES_STORAGE_KEY).filter(
      (item) => item.id !== id
    )
  );
  return listMessageTemplates(companyId);
}

export function contactPhone(contact: {
  phoneNumber?: string;
  whatsapp?: string;
}): string {
  return String(contact.phoneNumber || contact.whatsapp || "").trim();
}

/** Extrae la primera URL de un texto libre para autocompletar el campo de enlace. */
export function extractFirstUrl(text: string): string {
  const match = text.match(/https?:\/\/[^\s]+/i);
  return match ? match[0] : "";
}

export function linkHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//i, "").split("/")[0];
  }
}
